import apiUtils from "../../../utils/apiUtils";
import type { LoginRequest } from "../data/login-request.data";
import type { LoginResponseData } from "../data/login-response.data";
import type {
  ChangePasswordRequest,
  ResetPasswordRequest,
  SendEmailVerificationCodeRequest,
  SendEmailOtpRequest,
  SendPhoneOtpRequest,
  SendResetLinkRequest,
  VerifyEmailOtpRequest,
  VerifyPasswordResetRequest,
  VerifyPhoneOtpRequest,
  ValidateTokenData,
} from "../data/auth-api.types";
import { BaseUserResponse } from "../data";
import type {
  MerchantAccountFormData,
  CategoryResponse,
  MerchantBusinessFormData,
  MerchantBusinessProfileResponse,
} from "../merchant-register/types/merchantRegister.types";
import { normalizeApiError } from "../../../lib/result-codes";
import { BaseResponse } from "../../../lib/api-contract";
import { SendEmailVerificationCodeResponse } from "../data/email-verification-code-response";

export const authApi = {
  /**
   * Login with email and password
   */
  login: async (data: LoginRequest): Promise<BaseResponse<LoginResponseData>> => {
    try {
      return await apiUtils.post<LoginResponseData>("/v1/auth/login", data);
    } catch (error) {
      return normalizeApiError(error);
    }
  },

  /**
   * Send password reset link to user's email
   */
  sendResetPasswordLink: (data: SendResetLinkRequest): Promise<boolean> =>
    apiUtils.postData<boolean>(
      "/v1/auth/password-reset/request",
      data,
      "Failed to send password reset link",
    ),

  verifyPasswordReset: (data: VerifyPasswordResetRequest): Promise<boolean> =>
    apiUtils.postData<boolean>(
      "/v1/auth/password-reset/verify",
      data,
      "Failed to verify password reset",
    ),

  requestPhoneOtp: (data: SendPhoneOtpRequest): Promise<boolean> =>
    apiUtils.postData<boolean>("/v1/auth/otp/phone/send", data, "Failed to send phone OTP"),

  verifyPhoneOtp: (data: VerifyPhoneOtpRequest): Promise<boolean> =>
    apiUtils.postData<boolean>("/v1/auth/otp/phone/verify", data, "Failed to verify phone OTP"),

  requestEmailOtp: (data: SendEmailOtpRequest): Promise<boolean> =>
    apiUtils.postData<boolean>("/v1/auth/otp/email/send", data, "Failed to send email OTP"),

  verifyEmailOtp: (data: VerifyEmailOtpRequest): Promise<boolean> =>
    apiUtils.postData<boolean>("/v1/auth/otp/email/verify", data, "Failed to verify email OTP"),

  /**
   * Validate reset token
   */
  validateResetToken: (token: string): Promise<ValidateTokenData> =>
    apiUtils.getData<ValidateTokenData>(
      `/v1/auth/validate-reset-token/${token}`,
      "Invalid or expired reset token",
    ),

  /**
   * Reset password with token
   */
  resetPasswordWithToken: (data: ResetPasswordRequest): Promise<boolean> =>
    apiUtils.postData<boolean>("/v1/auth/reset-password", data, "Failed to reset password"),

  /**
   * Change user password
   */
  changePassword: async (userId: string, data: ChangePasswordRequest): Promise<BaseUserResponse> =>
    apiUtils.putData<BaseUserResponse>(
      `/v1/user/password/update/${userId}`,
      data,
      "Failed to change password",
    ),

  getMerchantCategories: (signal?: AbortSignal): Promise<CategoryResponse[]> =>
    apiUtils.getData<CategoryResponse[]>(
      "/v1/category/merchant",
      "Failed to fetch merchant categories",
      { signal },
    ),
  sendMerchantVerificationCode: async (
    email: string,
  ): Promise<BaseResponse<SendEmailVerificationCodeResponse>> => {
    const request: SendEmailVerificationCodeRequest = {
      email,
      target: "register_merchant",
    };

    try {
      const result = await apiUtils.post<SendEmailVerificationCodeResponse>(
        "/v1/auth/otp/email/send",
        request,
      );
      return result;
    } catch (error) {
      return normalizeApiError(error);
    }
  },
  verifyMerchantEmail: async (email: string, code: string): Promise<BaseResponse<unknown>> => {
    try {
      const result = await apiUtils.post<boolean>("/v1/auth/otp/email/verify", {
        email,
        target: "register_merchant",
        code,
      });
      return result;
    } catch (error) {
      return normalizeApiError(error);
    }
  },
  registerMerchantAccount: async (
    data: MerchantAccountFormData,
  ): Promise<BaseResponse<LoginResponseData>> => {
    try {
      return await apiUtils.post<LoginResponseData>("/v1/auth/merchant-register", {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        phone_number: data.phoneNumber,
      });
    } catch (error) {
      return normalizeApiError(error);
    }
  },

  createMerchantBusinessProfile: (data: MerchantBusinessFormData) => {
    const formData = new FormData();
    formData.append("business_name", data.businessName);
    formData.append("short_intro", data.businessShortIntro);
    formData.append("business_category_id", data.businessCategory);
    formData.append("partner_type", data.partnerType);

    if (data.description) formData.append("description", data.description);
    if (data.businessUrl) formData.append("business_url", data.businessUrl);
    if (data.logoImage) formData.append("logo_file", data.logoImage);

    data.addresses.forEach((address, index) => {
      const prefix = `locations[${index}]`;
      formData.append(`${prefix}.address`, address.address1);
      formData.append(`${prefix}.city`, address.city);
      formData.append(`${prefix}.state`, address.state);
      formData.append(`${prefix}.zip_code`, address.zipcode);
      if (address.latitude !== undefined) {
        formData.append(`${prefix}.latitude`, String(address.latitude));
      }
      if (address.longitude !== undefined) {
        formData.append(`${prefix}.longitude`, String(address.longitude));
      }
    });

    return apiUtils.postWithFormData<MerchantBusinessProfileResponse>(
      "/v1/local-merchant",
      formData,
    );
  },
};
